import React, { FC, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  Divider,
  Flex,
  Heading,
  Text,
  Tile,
  TileControl,
  TileGroup,
} from '@amzn/storm-ui';
import CombineAccountsLayout from '@features/combine-accounts/components/CombineAccountsLayout';
import CombineAccountsSteps, {
  CombineAccountsStep,
} from '@features/combine-accounts/components/CombineAccountsSteps';
import CombineAccountsActionButtons from '@features/combine-accounts/components/CombineAccountsActionButtons';
import {
  COMBINE_ACCOUNTS_LANDING_PATH,
  COMBINE_ACCOUNTS_CHOOSE_PATH,
  COMBINE_ACCOUNTS_BILLING_PATH,
} from 'src/shared/routes';
import {
  COMBINE_ACCOUNTS_NEXT_KEY,
  COMBINE_ACCOUNTS_DETAILS_HEADING_KEY,
  COMBINE_ACCOUNTS_DETAILS_DESCRIPTION_KEY,
  COMBINE_ACCOUNTS_DETAILS_BUSINESS_NAME_KEY,
  COMBINE_ACCOUNTS_DETAILS_PHONE_NUMBER_KEY,
  COMBINE_ACCOUNTS_DETAILS_WEBSITE_KEY,
  COMBINE_ACCOUNTS_DETAILS_ADDRESS_KEY,
  COMBINE_ACCOUNTS_DETAILS_BILLING_REG_NUM_KEY,
  COMBINE_ACCOUNTS_DETAILS_CURRENCY_KEY,
  COMBINE_ACCOUNTS_DETAILS_TIMEZONE_KEY,
  COMBINE_ACCOUNTS_DETAILS_INDUSTRY_KEY,
  COMBINE_ACCOUNTS_DETAILS_DISPLAY_NAME_KEY,
  COMBINE_ACCOUNTS_DETAILS_TILE_TITLE_KEY,
  COMBINE_ACCOUNTS_DETAILS_LOADING_KEY,
  COMBINE_ACCOUNTS_DETAILS_ERROR_KEY,
  COMBINE_ACCOUNTS_DETAILS_MISSING_PARAMS_KEY,
  COMBINE_ACCOUNTS_DETAILS_TYPE_SA_KEY,
  COMBINE_ACCOUNTS_DETAILS_TYPE_DSP_KEY,
  GA_PARAM,
  CFID_PARAM,
  ACCOUNT_TYPE_SA,
  ACCOUNT_TYPE_DSP,
} from '@features/combine-accounts/constants';
import useIntl from '@i18n/useIntl';
import ReadOnlyFormField from 'src/shared/components/form/ReadOnlyFormRow';
import { useGetAccountDetailsQuery } from 'src/shared/services/combine-accounts/combineAccounts';
import type {
  SaBusinessDetails,
  DspAccountDetails,
} from 'src/shared/services/combine-accounts/types';

const PAGE_ID = 'combine-accounts-details-page';

/** Joins non-empty address parts into a single display string. */
const formatAddress = (
  details: SaBusinessDetails | DspAccountDetails,
): string =>
  [
    details.addressLine1,
    details.addressLine2,
    details.city,
    details.state,
    details.postalCode,
    details.countryCode,
  ]
    .filter(Boolean)
    .join(', ');

interface AccountTileDetails {
  business: {
    businessName: string;
    phone: string;
    website: string;
    countryCode: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipCode: string;
    businessRegistrationNumber: string;
  };
  currency: string;
  timezone: string;
  industry: string;
  displayName: string;
  /** Pre-formatted address for read-only display */
  address: string;
}

interface AccountTile {
  name: string;
  type: typeof ACCOUNT_TYPE_SA | typeof ACCOUNT_TYPE_DSP;
  typeLabel: string;
  id: string;
  details: AccountTileDetails;
}

const CombineAccountsDetailsPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { intl } = useIntl();

  const globalAccountId = searchParams.get(GA_PARAM);
  const cfid = searchParams.get(CFID_PARAM);

  const { data, isLoading, isError } = useGetAccountDetailsQuery(
    { globalAccountId: globalAccountId ?? '', cfid: cfid ?? '' },
    { skip: !globalAccountId || !cfid },
  );

  const saLabel = intl.getMessage(COMBINE_ACCOUNTS_DETAILS_TYPE_SA_KEY);
  const dspLabel = intl.getMessage(COMBINE_ACCOUNTS_DETAILS_TYPE_DSP_KEY);

  const accounts: AccountTile[] = useMemo(() => {
    if (!data) return [];
    const tiles: AccountTile[] = [];
    if (data.saBusinessDetails) {
      const sa = data.saBusinessDetails;
      tiles.push({
        name: sa.businessName ?? '',
        type: ACCOUNT_TYPE_SA,
        typeLabel: saLabel,
        id: globalAccountId ?? '',
        details: {
          business: {
            businessName: sa.businessName ?? '',
            phone: sa.phoneNumber ?? '',
            website: sa.website ?? '',
            countryCode: sa.countryCode ?? '',
            addressLine1: sa.addressLine1 ?? '',
            addressLine2: sa.addressLine2 ?? '',
            city: sa.city ?? '',
            state: sa.state ?? '',
            zipCode: sa.postalCode ?? '',
            businessRegistrationNumber: sa.billingRegistrationNumber ?? '',
          },
          currency: sa.currency,
          timezone: sa.timezone,
          industry: sa.industry,
          displayName: sa.displayName ?? '',
          address: formatAddress(sa),
        },
      });
    }
    if (data.dspAccountDetails) {
      const dsp = data.dspAccountDetails;
      tiles.push({
        name: dsp.businessName ?? '',
        type: ACCOUNT_TYPE_DSP,
        typeLabel: dspLabel,
        id: cfid ?? '',
        details: {
          business: {
            businessName: dsp.businessName ?? '',
            phone: dsp.phoneNumber ?? '',
            website: dsp.website ?? '',
            countryCode: dsp.countryCode ?? '',
            addressLine1: dsp.addressLine1 ?? '',
            addressLine2: dsp.addressLine2 ?? '',
            city: dsp.city ?? '',
            state: dsp.state ?? '',
            zipCode: dsp.postalCode ?? '',
            businessRegistrationNumber: '',
          },
          currency: dsp.currency ?? '',
          timezone: dsp.timezone ?? '',
          industry: dsp.industry ?? '',
          displayName: dsp.businessName ?? '',
          address: formatAddress(dsp),
        },
      });
    }
    return tiles;
  }, [data, globalAccountId, cfid, saLabel, dspLabel]);

  // Initialize from URL param so the previously-selected account is preserved
  // when navigating back from the billing page.
  const [selectedAccountId, setSelectedAccountId] = useState<string>(
    () => searchParams.get('selectedAccountId') ?? '',
  );

  // Derive effective selection synchronously to avoid an intermediate render
  // where accounts has populated but selectedAccountId is still stale/empty.
  const effectiveSelectedId = accounts.some((a) => a.id === selectedAccountId)
    ? selectedAccountId
    : accounts[0]?.id ?? '';

  const selectedAccount = accounts.find((a) => a.id === effectiveSelectedId);
  const details = selectedAccount?.details;

  const handleCancel = () => navigate(COMBINE_ACCOUNTS_LANDING_PATH);
  const handleGoBack = () =>
    navigate(`${COMBINE_ACCOUNTS_CHOOSE_PATH}?${searchParams.toString()}`);
  const handleNext = () => {
    const params = new URLSearchParams(searchParams);
    params.set('selectedAccountId', effectiveSelectedId);

    navigate(`${COMBINE_ACCOUNTS_BILLING_PATH}?${params.toString()}`, {
      state: { accountType: selectedAccount?.type },
    });
  };
  const handleClose = () => navigate(COMBINE_ACCOUNTS_LANDING_PATH);

  const handleTileChange = ([value]: string[]) => {
    if (value) setSelectedAccountId(value);
  };

  const actionButtons = (
    <CombineAccountsActionButtons
      onCancel={handleCancel}
      onGoBack={handleGoBack}
      onSubmit={handleNext}
      submitDisabled={!selectedAccount}
      submitLabelKey={COMBINE_ACCOUNTS_NEXT_KEY}
    />
  );

  if (isLoading) {
    return (
      <Box id={PAGE_ID} data-testid={PAGE_ID}>
        <CombineAccountsLayout onClose={handleClose}>
          <Text>{intl.getMessage(COMBINE_ACCOUNTS_DETAILS_LOADING_KEY)}</Text>
        </CombineAccountsLayout>
      </Box>
    );
  }

  if (!globalAccountId || !cfid) {
    return (
      <Box id={PAGE_ID} data-testid={PAGE_ID}>
        <CombineAccountsLayout onClose={handleClose}>
          <Text color="secondary">
            {intl.getMessage(COMBINE_ACCOUNTS_DETAILS_MISSING_PARAMS_KEY)}
          </Text>
        </CombineAccountsLayout>
      </Box>
    );
  }

  if (isError || (!isLoading && !data)) {
    return (
      <Box id={PAGE_ID} data-testid={PAGE_ID}>
        <CombineAccountsLayout onClose={handleClose}>
          <Text color="secondary">
            {intl.getMessage(COMBINE_ACCOUNTS_DETAILS_ERROR_KEY)}
          </Text>
        </CombineAccountsLayout>
      </Box>
    );
  }

  if (accounts.length === 0) {
    return (
      <Box id={PAGE_ID} data-testid={PAGE_ID}>
        <CombineAccountsLayout onClose={handleClose}>
          <Text color="secondary">
            {intl.getMessage(COMBINE_ACCOUNTS_DETAILS_ERROR_KEY)}
          </Text>
        </CombineAccountsLayout>
      </Box>
    );
  }

  const detailFields = details
    ? [
        {
          labelKey: COMBINE_ACCOUNTS_DETAILS_BUSINESS_NAME_KEY,
          key: 'businessName',
          value: details.business.businessName,
        },
        {
          labelKey: COMBINE_ACCOUNTS_DETAILS_PHONE_NUMBER_KEY,
          key: 'phoneNumber',
          value: details.business.phone,
        },
        {
          labelKey: COMBINE_ACCOUNTS_DETAILS_WEBSITE_KEY,
          key: 'website',
          value: details.business.website,
        },
        {
          labelKey: COMBINE_ACCOUNTS_DETAILS_ADDRESS_KEY,
          key: 'address',
          value: details.address,
        },
        {
          labelKey: COMBINE_ACCOUNTS_DETAILS_BILLING_REG_NUM_KEY,
          key: 'billingRegNum',
          value: details.business.businessRegistrationNumber,
        },
        {
          labelKey: COMBINE_ACCOUNTS_DETAILS_CURRENCY_KEY,
          key: 'currency',
          value: details.currency,
        },
        {
          labelKey: COMBINE_ACCOUNTS_DETAILS_TIMEZONE_KEY,
          key: 'timezone',
          value: details.timezone,
        },
        {
          labelKey: COMBINE_ACCOUNTS_DETAILS_INDUSTRY_KEY,
          key: 'industry',
          value: details.industry,
        },
        {
          labelKey: COMBINE_ACCOUNTS_DETAILS_DISPLAY_NAME_KEY,
          key: 'displayName',
          value: details.displayName,
        },
      ]
    : [];

  return (
    <Box id={PAGE_ID} data-testid={PAGE_ID}>
      <CombineAccountsLayout
        actionButtons={actionButtons}
        onClose={handleClose}
      >
        <Box paddingBlockEnd="small">
          <CombineAccountsSteps current={CombineAccountsStep.AccountDetails} />
        </Box>

        <Card>
          <Box
            paddingInlineStart="medium"
            paddingInlineEnd="medium"
            paddingBlockStart="medium"
            paddingBlockEnd="medium"
          >
            <Heading renderAs="h4" typeface="display">
              {intl.getMessage(COMBINE_ACCOUNTS_DETAILS_HEADING_KEY)}
            </Heading>
            <Text color="secondary">
              {intl.getMessage(COMBINE_ACCOUNTS_DETAILS_DESCRIPTION_KEY)}
            </Text>

            <Box paddingBlockStart="small" paddingBlockEnd="small">
              <TileGroup
                name="inherit-account"
                selectedValues={[effectiveSelectedId]}
                onChange={handleTileChange}
                multiple={false}
                numColumns={2}
              >
                {accounts.map((account) => (
                  <Tile key={account.id} label={account.id} value={account.id}>
                    <TileControl
                      title={intl.formatMessage(
                        COMBINE_ACCOUNTS_DETAILS_TILE_TITLE_KEY,
                        {
                          accountName: account.name,
                          accountType: account.typeLabel,
                        },
                      )}
                      text={account.id}
                    />
                  </Tile>
                ))}
              </TileGroup>
            </Box>

            <Divider marginBlockEnd="xsmall" />

            <Box>
              {detailFields.map(({ labelKey, key, value }) => (
                <ReadOnlyFormField
                  key={key}
                  id={`${PAGE_ID}-${key}`}
                  label={intl.getMessage(labelKey)}
                  value={value}
                />
              ))}
            </Box>
          </Box>
        </Card>

        <Flex justifyContent="flex-end" paddingBlockStart="small">
          {actionButtons}
        </Flex>
      </CombineAccountsLayout>
    </Box>
  );
};

export default CombineAccountsDetailsPage;
